/**
 * Organization Context Utility
 * Extracts organization from URL path or subdomain
 * Supports both path-based (/kitchener/signup) and subdomain-based (kitchener.followup.wcitoronto.ca)
 */

/**
 * Extract organization slug from URL path
 * Examples:
 *   /kitchener/signup -> 'kitchener'
 *   /toronto/dashboard -> 'toronto'
 *   /signup -> null (no org in path)
 */
export const getOrganizationFromPath = (pathname) => {
  // Remove leading slash and split
  const segments = pathname.split('/').filter(Boolean);
  
  // If first segment is a valid organization slug, return it
  if (segments.length > 0) {
    const firstSegment = segments[0].toLowerCase();
    
    // Valid organization slugs (can be expanded or fetched from API)
    const validSlugs = [
      'toronto', 'kitchener', 'windsor', 'ottawa', 
      // Add more as needed, or fetch from API
    ];
    
    // Check if first segment matches a known organization slug
    // Or if it's not a known app route, assume it's an organization
    const knownAppRoutes = [
      'login', 'signup', 'dashboard', 'signup-success', 
      'submission-success', 'forgot-password', 'reset-password',
      'profile', 'user-management', 'assignments', 'my-tasks',
      'new-convert', 'new-converts', 'assign-new-converts', 'thank-you',
      'my-new-converts', 'sms', 'harvest', 'harvest-system',
      'email-campaigns', 'email-composer', 'add-first-timer',
      'admin', 'master-dashboard', 'organization-management', 'organization-settings', 'not-found'
    ];
    
    if (!knownAppRoutes.includes(firstSegment)) {
      return firstSegment; // Assume it's an organization slug
    }
  }
  
  return null;
};

/**
 * Extract organization slug from subdomain
 * Examples:
 *   kitchener.followup.wcitoronto.ca -> 'kitchener'
 *   toronto.followup.wcitoronto.ca -> 'toronto'
 *   followup.wcitoronto.ca -> null (no subdomain)
 *   dev.followup.wcitoronto.ca -> null (environment subdomain, not org)
 */
export const getOrganizationFromSubdomain = () => {
  const hostname = window.location.hostname;
  
  // Split by dots
  const parts = hostname.split('.');
  
  // If we have subdomain (more than 2 parts), first part is org
  // e.g., kitchener.followup.wcitoronto.ca
  // parts = ['kitchener', 'followup', 'wcitoronto', 'ca']
  if (parts.length > 2) {
    const subdomain = parts[0];
    
    // Skip environment/deployment subdomains and common prefixes
    const environmentSubdomains = ['www', 'app', 'dev', 'staging', 'test', 'qa', 'prod', 'production'];
    if (subdomain && !environmentSubdomains.includes(subdomain.toLowerCase())) {
      return subdomain.toLowerCase();
    }
  }
  
  return null;
};

/**
 * Get current organization from URL (checks both path and subdomain)
 * Priority: Path > Subdomain (path takes precedence when present)
 * Returns null if no organization is found in URL (user should select from welcome page)
 */
export const getCurrentOrganization = () => {
  // First, try path (path takes precedence for clarity)
  // This handles cases like dev.followup.wcitoronto.ca/ottawa/signup
  const fromPath = getOrganizationFromPath(window.location.pathname);
  if (fromPath) {
    return fromPath;
  }
  
  // Then, try subdomain (for cases like ottawa.followup.wcitoronto.ca/signup)
  const fromSubdomain = getOrganizationFromSubdomain();
  if (fromSubdomain) {
    return fromSubdomain;
  }
  
  // No organization found in URL - return null
  // Components should handle this by redirecting to welcome page or showing organization selection
  return null;
};

/**
 * Get organization ID from slug
 * Fetches from API or uses cached data
 */
let organizationsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const fetchOrganizationsList = async () => {
  if (organizationsCache && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_DURATION) {
    return organizationsCache;
  }

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3003';
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  const response = await fetch(`${apiUrl}/api/organizations`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: controller.signal,
  });

  clearTimeout(timeoutId);

  if (!response.ok) {
    throw new Error(`Failed to fetch organizations: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  if (data.organizations && Array.isArray(data.organizations)) {
    organizationsCache = data.organizations;
    cacheTimestamp = Date.now();
    return organizationsCache;
  }

  console.error('Invalid response format from organizations API:', data);
  return [];
};

export const getOrganizationBySlug = async (slug) => {
  if (!slug) return null;

  try {
    const organizations = await fetchOrganizationsList();
    return organizations.find((o) => o.slug === slug) || null;
  } catch (error) {
    console.error('Error fetching organization by slug:', error);
    return null;
  }
};

export const formatOrganizationDisplayName = (slug, name) => {
  if (name) return name;
  if (!slug) return 'Winners Chapel International Canada';
  return `Winners Chapel ${slug.charAt(0).toUpperCase()}${slug.slice(1)}`;
};

export const getOrganizationId = async (slug) => {
  const org = await getOrganizationBySlug(slug);
  return org?.id ?? null;
};

/**
 * Build URL with organization context
 * Example: buildOrgUrl('/signup', 'kitchener') -> '/kitchener/signup'
 */
export const buildOrgUrl = (path, organizationSlug) => {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  // If path already starts with org slug, return as-is
  const pathSegments = cleanPath.split('/');
  if (pathSegments.length > 0 && pathSegments[0] === organizationSlug) {
    return `/${cleanPath}`;
  }
  
  // Otherwise, prepend organization slug
  return `/${organizationSlug}/${cleanPath}`;
};

/**
 * Remove organization slug from path
 * Example: removeOrgFromPath('/kitchener/signup') -> '/signup'
 */
export const removeOrgFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  
  if (segments.length > 0) {
    const firstSegment = segments[0].toLowerCase();
    const knownSlugs = ['toronto', 'kitchener', 'windsor', 'ottawa'];
    
    if (knownSlugs.includes(firstSegment)) {
      // Remove organization slug
      return '/' + segments.slice(1).join('/');
    }
  }
  
  return pathname;
};

